import { Box, TextField, Typography, Checkbox, FormControlLabel } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import DateTimePicker from '@mui/lab/DateTimePicker';
import { debounce } from 'lodash';
import { TextEditor, FileUploader } from 'ui';
import { FieldControlProps, SelectOption } from '../types/eventsTypes';
import { EventsDocumentsControl } from './EventsDocumentsControl/EventsDocumentsControl';

export const FieldControl: FC<FieldControlProps> = ({
  name,
  type,
  label,
  options,
  disabled,
  error,
  value,
  onChange,
  onUpdate,
}) => {
  const { t } = useTranslation();

  /* *********************************************************************** */
  /*                                  Date                                   */
  /* *********************************************************************** */
  if (type === 'date-time') {
    return (
      <DateTimePicker
        inputFormat="h:mm a dd/MM/yyyy"
        renderInput={() => <TextField label={label} error={!!error} />}
        value={(value as Date) || null}
        onChange={val => onChange(name, val)}
        disabled={disabled}
      />
    );
  }

  /* *********************************************************************** */
  /*                              Autocomplete                               */
  /* *********************************************************************** */
  if (type === 'autocomplete') {
    return (
      <Autocomplete
        options={options || []}
        getOptionLabel={(option: SelectOption) => option.name}
        isOptionEqualToValue={(option: SelectOption, val: SelectOption) => option.id === val.id}
        onChange={(_, val) => onChange(name, val)}
        onInputChange={debounce((_, val) => onUpdate && onUpdate(name, val), 500)}
        value={(value as SelectOption) || null}
        renderInput={params => (
          <TextField
            {...params}
            error={!!error}
            label={t(label)}
            variant="outlined"
            disabled={disabled}
          />
        )}
        disabled={disabled || !options}
      />
    );
  }

  /* *********************************************************************** */
  /*                                Rich text                                */
  /* *********************************************************************** */
  if (type === 'rich-text') {
    return (
      <>
        <Box mt={2}>
          <Typography variant="body1" component="span">
            {t(label)}
          </Typography>
        </Box>
        <Box mt={1}>
          <TextEditor
            error={!!error}
            placeholder={t(label)}
            value={value as string}
            disabled={disabled}
            onChange={val => onChange(name, val)}
          />
        </Box>
      </>
    );
  }

  /* *********************************************************************** */
  /*                                Checkbox                                 */
  /* *********************************************************************** */
  if (type === 'checkbox') {
    return (
      <Box mt={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={(value as boolean) || false}
              onChange={(_, val) => onChange(name, val)}
              name={name}
              color="primary"
              disabled={disabled}
            />
          }
          label={label}
        />
      </Box>
    );
  }

  /* *********************************************************************** */
  /*                                 Number                                  */
  /* *********************************************************************** */
  if (type === 'number') {
    return (
      <TextField
        name={name}
        fullWidth
        type="number"
        label={t(label)}
        variant="outlined"
        error={!!error}
        value={value || ''}
        onChange={event => onChange(name, event.target.value)}
        disabled={disabled}
      />
    );
  }

  /* *********************************************************************** */
  /*                                  Image                                  */
  /* *********************************************************************** */
  if (type === 'image') {
    return (
      <Box mt={2}>
        <Typography variant="body1" component="span">
          {t(label)}
        </Typography>
        <Box mt={2}>
          <FileUploader
            value={value ? [value] : []}
            onChange={img => onChange(name, img[0])}
            error={!!error}
          />
        </Box>
      </Box>
    );
  }

  /* *********************************************************************** */
  /*                                Documents                                */
  /* *********************************************************************** */
  if (type === 'documents') {
    return (
      <EventsDocumentsControl
        label={label}
        value={value as any[]}
        onChange={docs => onChange(name, docs)}
      />
    );
  }

  /* *********************************************************************** */
  /*                             Text (default)                              */
  /* *********************************************************************** */
  return (
    <TextField
      name={name}
      fullWidth
      label={t(label)}
      variant="outlined"
      error={!!error}
      value={value || ''}
      onChange={event => onChange(name, event.target.value)}
      disabled={disabled}
    />
  );
};
